/**
 * CategoryBreakdown — expandable list of expense categories with per-vendor
 * drill-down. Extracted from Dashboard.jsx in iter 60. `COLORS` and
 * `ChevronDown` icons injected as regular imports (previously pulled from
 * the Dashboard scope).
 */
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import api from "@/lib/api";

// Same colour palette Dashboard uses for the category pie chart. Kept here
// so the swatch beside each row matches the chart.
const COLORS = [
  "#0891b2", "#059669", "#d97706", "#dc2626", "#7c3aed",
  "#db2777", "#65a30d", "#0284c7", "#ca8a04", "#9333ea",
];

export default function CategoryBreakdown({
  categories,
  catMap: _catMap,  // unused — was passed from parent for legacy reasons
  lang,
  baseCurrency,
  formatMoney,
  summaryYear,
}) {
  const [expandedKey, setExpandedKey] = useState(null);
  const [invoicesByKey, setInvoicesByKey] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);

  // Invalidate the drill-down cache whenever the global year filter changes.
  useEffect(() => {
    setInvoicesByKey({});
    setExpandedKey(null);
  }, [summaryYear]);

  const grandTotal = categories.reduce((s, c) => s + (c.value || 0), 0);

  const toggle = async (key, raw) => {
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (invoicesByKey[key]) return;
    setLoadingKey(key);
    try {
      if (raw === "salaries") {
        const params = new URLSearchParams();
        if (summaryYear) params.set("year", String(summaryYear));
        const { data } = await api.get(`/salary-entries?${params.toString()}`);
        const entries = Array.isArray(data?.entries) ? data.entries : [];
        const normalized = entries.map((e) => ({
          id: e.id,
          vendor: e.concept || (lang === "es" ? "Salario" : "Salary"),
          date: e.date,
          amount: e.amount,
          subtotal: e.amount,
          tax: 0,
          currency: e.currency || baseCurrency,
          _isSalary: true,
        }));
        setInvoicesByKey((prev) => ({ ...prev, [key]: normalized }));
        return;
      }
      const params = new URLSearchParams();
      params.set("type", "expense");
      params.set("category", raw);
      params.set("limit", "1000");
      if (summaryYear) {
        params.set("start", `${summaryYear}-01-01`);
        params.set("end", `${summaryYear}-12-31`);
      }
      const { data } = await api.get(`/invoices?${params.toString()}`);
      setInvoicesByKey((prev) => ({ ...prev, [key]: Array.isArray(data) ? data : [] }));
    } catch {
      setInvoicesByKey((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingKey(null);
    }
  };

  const groupByVendor = (invoices, currency) => {
    const map = new Map();
    for (const inv of invoices) {
      const vendor = inv.vendor || "—";
      const base = inv.subtotal != null ? inv.subtotal : (inv.amount || 0) - (inv.tax || 0);
      const sign = inv.is_credit_note ? -1 : 1;
      const cur = inv.currency || currency;
      const existing = map.get(vendor) || { vendor, total: 0, count: 0, currency: cur };
      existing.total += base * sign;
      existing.count += 1;
      map.set(vendor, existing);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  };

  return (
    <div className="divide-y divide-[var(--border-color)]" data-testid="dashboard-category-breakdown">
      {categories.map((cat, idx) => {
        const key = cat.raw || cat.name || `cat-${idx}`;
        const pct = grandTotal > 0 ? Math.round((cat.value / grandTotal) * 100) : 0;
        const isOpen = expandedKey === key;
        const invoices = invoicesByKey[key] || [];
        const grouped = isOpen ? groupByVendor(invoices, baseCurrency) : [];
        return (
          <div key={key}>
            <button
              type="button"
              onClick={() => toggle(key, cat.raw || cat.name)}
              className="w-full flex items-center justify-between px-1 py-3 hover:bg-[var(--surface-hover)] transition-colors text-left"
              data-testid={`category-row-${key}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span
                  className="w-3 h-3 rounded-sm shrink-0"
                  style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                />
                <span className="text-sm font-medium truncate">{cat.name}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-[var(--text-muted)] font-mono-num w-10 text-right">{pct}%</span>
                <span className="text-sm font-medium font-mono-num min-w-24 text-right">
                  {formatMoney(cat.value, baseCurrency)}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-[var(--text-muted)] transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </div>
            </button>
            {isOpen && (
              <div className="ml-5 mr-2 pb-3 space-y-1" data-testid={`category-drill-${key}`}>
                {loadingKey === key ? (
                  <div className="text-xs text-[var(--text-muted)] py-2">
                    {lang === "es" ? "Cargando facturas..." : "Loading invoices..."}
                  </div>
                ) : grouped.length === 0 ? (
                  <div className="text-xs text-[var(--text-muted)] py-2">
                    {lang === "es" ? "Sin facturas en esta categoría" : "No invoices in this category"}
                  </div>
                ) : (
                  grouped.slice(0, 50).map((g) => (
                    <div
                      key={g.vendor}
                      className="flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-[var(--surface-hover)]"
                      data-testid={`category-vendor-row-${key}-${g.vendor}`}
                    >
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        <span className="font-medium truncate">{g.vendor}</span>
                        <span className="text-[10px] text-[var(--text-muted)] shrink-0">
                          · {g.count} {cat.raw === "salaries"
                            ? (lang === "es" ? "nóm." : "pay.")
                            : (lang === "es" ? "fact." : "inv.")}
                        </span>
                      </div>
                      <span className="font-mono-num text-[var(--text-primary)] shrink-0 ml-2">
                        {formatMoney(g.total, g.currency)}
                      </span>
                    </div>
                  ))
                )}
                {grouped.length > 50 && (
                  <div className="text-[10px] text-[var(--text-muted)] px-2 pt-1">
                    {lang === "es" ? `+${grouped.length - 50} más...` : `+${grouped.length - 50} more...`}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
